import { useEffect } from "react";
import "../assets/css/chatStructuredReply.css";

const tryParse = (value) => {
  if (!value || typeof value !== "string") return null;
  const trimmed = value.trim();
  const fence = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const candidate = (fence ? fence[1] : trimmed).trim();
  const slices = [candidate];
  const start = candidate.indexOf("{");
  const end = candidate.lastIndexOf("}");
  if (start >= 0 && end > start) slices.push(candidate.slice(start, end + 1));
  const arrayStart = candidate.indexOf("[");
  const arrayEnd = candidate.lastIndexOf("]");
  if (arrayStart >= 0 && arrayEnd > arrayStart) {
    slices.push(candidate.slice(arrayStart, arrayEnd + 1));
  }
  for (const slice of slices) {
    try {
      return JSON.parse(slice);
    } catch {
      /* keep trying */
    }
  }
  return null;
};

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const humanize = (key) =>
  String(key || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const asList = (value) => {
  if (Array.isArray(value)) return value.filter((item) => item != null && item !== "");
  if (value == null || value === "") return [];
  return [value];
};

const optionLetter = (index) => String.fromCharCode(65 + index);

const FieldChips = ({ data, keys }) => {
  const items = keys
    .map((key) => ({ key, value: data?.[key] }))
    .filter((item) => item.value != null && item.value !== "");
  if (!items.length) return null;
  return (
    <div className="csr-chips">
      {items.map((item) => (
        <span className="csr-chip" key={item.key}>
          <em>{humanize(item.key)}</em> {String(item.value)}
        </span>
      ))}
    </div>
  );
};

const StandardsBlock = ({ data, onSave, onCancel }) => (
  <div className="csr-stack">
    <FieldChips data={data} keys={["framework", "state", "versionName", "grade", "subject", "strand"]} />
    {asList(data.standards).map((standard, index) => (
      <div className="csr-card" key={`${standard.code || "std"}-${index}`}>
        <div className="csr-card-head">
          {standard.code ? <span className="csr-code">{standard.code}</span> : <span className="csr-code">Standard {index + 1}</span>}
        </div>
        {standard.description ? <p>{standard.description}</p> : null}
        {standard.existsInCatalog ? <span className="csr-chip soft">In catalog</span> : null}
        {standard.grade || standard.subject || standard.framework || standard.versionName ? (
          <div className="csr-chips">
            {standard.grade ? <span className="csr-chip">{standard.grade === "K" ? "K" : `Grade ${standard.grade}`}</span> : null}
            {standard.subject ? <span className="csr-chip soft">{standard.subject}</span> : null}
            {standard.framework ? <span className="csr-chip soft">{standard.framework}</span> : null}
            {standard.versionName ? <span className="csr-chip">{standard.versionName}</span> : null}
          </div>
        ) : null}
        {asList(standard.matchedTopics).length ? (
          <div className="csr-chips">
            {asList(standard.matchedTopics).map((topic, topicIndex) => (
              <span className="csr-chip soft" key={topicIndex}>
                {typeof topic === "string" ? topic : topic.name || topic.ccssCode || JSON.stringify(topic)}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    ))}
    {data.pendingSave && !data.savedStandards && !data.discarded ? (
      <div className="csr-actions">
        <p className="csr-muted">
          Review these standards, then save them to {data.versionName || data.state || "the selected catalog version"}.
        </p>
        <div className="csr-action-row">
          <button type="button" className="csr-btn ghost" onClick={onCancel} disabled={!onCancel || data.saving}>
            Cancel
          </button>
          <button type="button" className="csr-btn" onClick={onSave} disabled={!onSave || data.saving || !data.versionId}>
            {data.saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    ) : null}
    {data.discarded ? <p className="csr-save warn">These standards were not saved.</p> : null}
  </div>
);

const QuestionsBlock = ({ data, onSave, onCancel, onAddVisuals, onSkipVisuals }) => {
  const questions = asList(data.questions);
  const generatingIndex = questions.findIndex((question) => question.generatingVisual);

  useEffect(() => {
    if (generatingIndex < 0) return undefined;
    const node = document.querySelector(`[data-ask-question="${generatingIndex}"]`);
    node?.scrollIntoView({ behavior: "smooth", block: "center" });
    return undefined;
  }, [generatingIndex]);

  return (
  <div className="csr-stack">
    <FieldChips data={data} keys={["grade", "subject", "strand", "topicName", "standardCode"]} />
    {questions.map((question, index) => {
      const options = asList(question.options);
      const answer = String(question.answer || question.correctAnswer || "").trim();
      const flagged = options.findIndex((option) => option && typeof option === "object" && option.isCorrect);
      const letterHit = /^[A-D]$/i.test(answer) ? answer.toUpperCase().charCodeAt(0) - 65 : -1;
      const textHit = options.findIndex((option) => {
        const text = typeof option === "string" ? option : option.text || option.label || "";
        return answer && text.toLowerCase() === answer.toLowerCase();
      });
      const answerIndex = flagged >= 0 ? flagged : letterHit >= 0 ? letterHit : textHit;
      const needsVisual = question.needsImage && !question.imageUrl && !question.questionImgName;
      return (
        <div
          className={`csr-card${question.generatingVisual ? " is-generating" : ""}`}
          data-ask-question={index}
          key={question.id || index}
        >
          <div className="csr-card-head">
            <strong>Question {index + 1}</strong>
            <span className="csr-chips">
              {question.type ? <span className="csr-chip soft">{humanize(question.type)}</span> : null}
              {question.difficulty ? <span className="csr-chip">{question.difficulty}</span> : null}
              {question.standardCode ? <span className="csr-chip soft">{question.standardCode}</span> : null}
              {question.approvalStatus ? <span className="csr-chip soft">{question.approvalStatus}</span> : null}
              {question.imageUrl || question.questionImgName ? <span className="csr-chip soft">Image</span> : null}
              {question.generatingVisual ? <span className="csr-chip soft">Generating</span> : null}
              {needsVisual && !question.generatingVisual ? <span className="csr-chip soft">Needs visual</span> : null}
            </span>
          </div>
          {question.generatingVisual ? (
            <div className="csr-image-loading" aria-live="polite">
              <span className="csr-image-dots" aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
              <p>Generating image…</p>
            </div>
          ) : question.imageUrl ? (
            <img className="csr-image" src={question.imageUrl} alt={question.altText || "Question illustration"} />
          ) : null}
          {question.imageError ? <p className="csr-error">{question.imageError}</p> : null}
          <p className="csr-stem">{question.stem || question.questionLine1 || question.question || ""}</p>
          {options.length ? (
            <ol className="csr-options">
              {options.map((option, optionIndex) => {
                const text = typeof option === "string" ? option : option.text || option.label || "";
                const letter = optionLetter(optionIndex);
                const selected = optionIndex === answerIndex;
                return (
                  <li key={optionIndex} className={selected ? "is-answer" : ""}>
                    <span>{letter}.</span> {text}
                    {selected ? <em>Answer</em> : null}
                  </li>
                );
              })}
            </ol>
          ) : null}
          {answer && !options.length ? (
            <p className="csr-answer">Answer: {answer}</p>
          ) : null}
        </div>
      );
    })}
    {data.pendingVisuals && !data.savedQuestions && !data.discarded && !data.visualsSkipped ? (
      <div className="csr-actions">
        <p className="csr-muted">
          {data.visualProgress || data.visualPrompt || "Add visuals to the questions that need them?"}
        </p>
        {data.visualError ? <p className="csr-error">{data.visualError}</p> : null}
        <div className="csr-action-row">
          <button
            type="button"
            className="csr-btn ghost"
            onClick={onSkipVisuals}
            disabled={!onSkipVisuals || data.addingVisuals || data.saving}
          >
            Skip visuals
          </button>
          <button
            type="button"
            className="csr-btn"
            onClick={onAddVisuals}
            disabled={!onAddVisuals || data.addingVisuals || data.saving}
          >
            {data.addingVisuals ? "Adding visuals…" : "Add visuals"}
          </button>
        </div>
      </div>
    ) : null}
    {data.visualsSkipped ? <p className="csr-muted">Saved as text-only unless you add visuals later.</p> : null}
    {data.pendingSave && !data.savedQuestions && !data.discarded ? (
      <div className="csr-actions">
        <p className="csr-muted">
          Review these questions, then save them as unapproved on {data.topicName || "the selected topic"}.
        </p>
        <div className="csr-action-row">
          <button type="button" className="csr-btn ghost" onClick={onCancel} disabled={!onCancel || data.saving || data.addingVisuals}>
            Cancel
          </button>
          <button type="button" className="csr-btn" onClick={onSave} disabled={!onSave || data.saving || data.addingVisuals || !data.topicId}>
            {data.saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    ) : null}
    {data.discarded && data.questions ? <p className="csr-save warn">These questions were not saved.</p> : null}
  </div>
  );
};

const ImageBlock = ({ data }) => (
  <div className="csr-card">
    <div className="csr-card-head">
      <strong>Generated image</strong>
    </div>
    <FieldChips data={data} keys={["style", "grade", "subject", "strand"]} />
    {data.imageUrl ? (
      <img className="csr-image" src={data.imageUrl} alt={data.altText || data.prompt || "Generated image"} />
    ) : (
      <p className="csr-muted">Generating the image failed. Showing the prompt instead.</p>
    )}
    {data.error ? <p className="csr-error">{data.error}</p> : null}
    {data.altText ? <p className="csr-muted">Alt text: {data.altText}</p> : null}
  </div>
);

const versionChoiceText = (data, row) =>
  data.linkExisting && data.state
    ? `Use version ${row.versionId}: ${row.versionName || "Catalog"}. Link this version to ${data.state}.`
    : [
        `Use version ${row.versionId}: ${row.versionName || "Catalog"}`,
        row.framework ? `framework ${row.framework}` : "",
        row.state ? `state ${row.state}` : "",
        data.grade ? `grade ${data.grade}` : "",
        data.subject ? `subject ${data.subject}` : "",
        data.strand ? `strand ${data.strand}` : "",
        "Create academic standards.",
      ]
        .filter(Boolean)
        .join(". ");

const versionActionText = (data, row) => {
  if (row.action === "link-existing") {
    return `Show existing catalog versions to link to ${data.state || "this state"}.`;
  }
  const framework = data.suggestedFramework?.code || data.framework || "";
  return `Create new catalog version for ${data.state || "this state"}${
    framework ? ` using ${framework}` : ""
  }.`;
};

const CatalogLookupBlock = ({ data, onChoice }) => {
  const hideVersionsUntilLink = asList(data.versionActions).length && !data.linkExisting;
  const states = asList(data.states).length
    ? asList(data.states)
    : asList(data.availableStates).length
      ? asList(data.availableStates)
      : hideVersionsUntilLink
        ? []
        : asList(data.versions);
  const creating = data.lookup === "create-setup" || data.lookup === "create-question";
  const summary = data.lookup === "question-summary";
  return (
    <div className="csr-stack">
      {data.message ? (
        <p className={summary ? "csr-save ok" : creating || data.standardCount === 0 ? "csr-save warn" : "csr-save ok"}>
          {data.message}
        </p>
      ) : null}
      <FieldChips
        data={data}
        keys={["topicName", "grade", "subject", "strand", "total", "approved", "unapproved", "enabled", "disabled", "questionCount", "standardCount", "state", "stateCode", "framework", "versionName"]}
      />
      {asList(data.needed).length ? (
        <div className="csr-chips">
          {asList(data.needed).map((item) => (
            <span className="csr-chip" key={item}>
              Needed: {humanize(item)}
            </span>
          ))}
        </div>
      ) : null}
      {data.grades?.length ? (
        <div className="csr-chips">
          {data.grades.map((grade) => (
            <span className="csr-chip soft" key={grade}>
              {grade === "K" ? "Kindergarten" : `Grade ${grade}`}
            </span>
          ))}
        </div>
      ) : null}
      {data.subjects?.length ? (
        <div className="csr-chips">
          {data.subjects.map((subject) => (
            <span className="csr-chip" key={subject}>
              {subject}
            </span>
          ))}
        </div>
      ) : null}
      {asList(data.versionActions).length ? (
        <div className="csr-stack">
          {asList(data.versionActions).map((row) => (
            <button
              type="button"
              className="csr-card csr-card-btn"
              key={row.action}
              onClick={() => onChoice?.(versionActionText(data, row))}
            >
              <div className="csr-card-head">
                <strong>{row.title}</strong>
                {data.suggestedFramework?.code ? (
                  <span className="csr-code">{data.suggestedFramework.code}</span>
                ) : null}
              </div>
              {row.subtitle ? <p className="csr-muted">{row.subtitle}</p> : null}
            </button>
          ))}
        </div>
      ) : null}
      {asList(data.frameworks).length ? (
        <div className="csr-chips">
          {asList(data.frameworks).map((item) => (
            <button
              type="button"
              className="csr-pick"
              key={item.code || item.name}
              onClick={() => onChoice?.(`Follow ${item.code} pattern. Create academic standards.`)}
            >
              {item.code} · {item.name}
            </button>
          ))}
        </div>
      ) : null}
      {!summary && asList(data.strands).length ? (
        <div className="csr-stack">
          {asList(data.strands).map((row, index) => {
            const card = (
              <>
                <div className="csr-card-head">
                  <strong>{row.strand || row.name || `Strand ${index + 1}`}</strong>
                </div>
                <div className="csr-chips">
                  {row.grade ? <span className="csr-chip">{`Grade ${row.grade}`}</span> : null}
                  {row.subject ? <span className="csr-chip soft">{row.subject}</span> : null}
                </div>
              </>
            );
            if (!onChoice || !(row.strandId || row.id || row.strand)) {
              return (
                <div className="csr-card" key={`${row.strandId || row.strand || index}`}>
                  {card}
                </div>
              );
            }
            return (
              <button
                type="button"
                className="csr-card csr-card-btn"
                key={`${row.strandId || row.strand}-${index}`}
                onClick={() => onChoice(strandChoiceText(data, row))}
              >
                {card}
              </button>
            );
          })}
        </div>
      ) : null}
      {states.length ? (
        <div className="csr-stack">
          {states.map((row, index) => {
            const card = (
              <>
                <div className="csr-card-head">
                  <strong>{row.versionName || row.state || "Catalog"}</strong>
                  {row.stateCode || row.framework ? (
                    <span className="csr-code">{row.stateCode || row.framework}</span>
                  ) : null}
                </div>
                <div className="csr-chips">
                  {row.state ? <span className="csr-chip">{row.state}</span> : null}
                  {row.framework ? (
                    <span className="csr-chip soft">{row.frameworkName || row.framework}</span>
                  ) : null}
                  {row.standardCount != null ? (
                    <span className="csr-chip">{row.standardCount} standards</span>
                  ) : null}
                </div>
              </>
            );
            if (!onChoice || !row.versionId) {
              return (
                <div className="csr-card" key={`${row.state || row.versionId || index}-${index}`}>
                  {card}
                </div>
              );
            }
            return (
              <button
                type="button"
                className="csr-card csr-card-btn"
                key={`${row.versionId}-${index}`}
                onClick={() => onChoice(versionChoiceText(data, row))}
              >
                {card}
              </button>
            );
          })}
        </div>
      ) : null}
      {!summary && asList(data.topics).length ? (
        <div className="csr-stack">
          {asList(data.topics).map((topic, index) => {
            const name = topic.topicName || topic.name || `Topic ${index + 1}`;
            const card = (
              <>
                <div className="csr-card-head">
                  <strong>{name}</strong>
                  {topic.ccssCode ? <span className="csr-code">{topic.ccssCode}</span> : null}
                </div>
                <div className="csr-chips">
                  {topic.grade ? <span className="csr-chip">{`Grade ${topic.grade}`}</span> : null}
                  {topic.subject ? <span className="csr-chip soft">{topic.subject}</span> : null}
                  {topic.strand ? <span className="csr-chip">{topic.strand}</span> : null}
                </div>
              </>
            );
            if (!onChoice || !(topic.topicId || topic.id)) {
              return (
                <div className="csr-card" key={`${topic.topicId || topic.id || index}`}>
                  {card}
                </div>
              );
            }
            return (
              <button
                type="button"
                className="csr-card csr-card-btn"
                key={`${topic.topicId || topic.id}-${index}`}
                onClick={() => onChoice(topicChoiceText(data, topic))}
              >
                {card}
              </button>
            );
          })}
        </div>
      ) : null}
      {asList(data.standards).length ? <StandardsBlock data={data} /> : null}
      {asList(data.questions).length ? <QuestionsBlock data={data} /> : null}
    </div>
  );
};

const strandChoiceText = (data, row) =>
  [
    row.strandId || row.id
      ? `Use strand ${row.strandId || row.id}: ${row.strand || row.name || "Strand"}`
      : `strand name ${row.strand || row.name || ""}`,
    data.grade || row.grade ? `grade ${data.grade || row.grade}` : "",
    data.subject || row.subject ? `subject ${data.subject || row.subject}` : "",
    data.lookup === "create-question" || data.lookup === "questions"
      ? "Create questions."
      : "Create academic standards.",
  ]
    .filter(Boolean)
    .join(". ");

const topicChoiceText = (data, row) =>
  [
    `Use topic ${row.topicId || row.id}: ${row.topicName || row.name || "Topic"}`,
    data.grade ? `grade ${data.grade}` : row.grade ? `grade ${row.grade}` : "",
    data.subject || row.subject ? `subject ${data.subject || row.subject}` : "",
    data.strand || row.strand ? `strand ${data.strand || row.strand}` : "",
    "Create questions.",
  ]
    .filter(Boolean)
    .join(". ");

const TopicsBlock = ({ data }) => (
  <div className="csr-stack">
    <FieldChips
      data={data}
      keys={["state", "stateCode", "framework", "grade", "subject", "strand", "version"]}
    />
    {asList(data.topics).map((topic, index) => (
      <div className="csr-card" key={index}>
        <div className="csr-card-head">
          <strong>{topic.name || topic.topicName || `Topic ${index + 1}`}</strong>
          {topic.ccssCode ? <span className="csr-code">{topic.ccssCode}</span> : null}
        </div>
      </div>
    ))}
  </div>
);

const GenericValue = ({ value }) => {
  if (value == null || value === "") return <span className="csr-muted">—</span>;
  if (typeof value === "boolean" || typeof value === "number") return <span>{String(value)}</span>;
  if (typeof value === "string") return <span>{value}</span>;
  if (Array.isArray(value)) {
    if (!value.length) return <span className="csr-muted">None</span>;
    if (value.every((item) => !isPlainObject(item))) {
      return (
        <div className="csr-chips">
          {value.map((item, index) => (
            <span className="csr-chip soft" key={index}>
              {String(item)}
            </span>
          ))}
        </div>
      );
    }
    return (
      <div className="csr-stack">
        {value.map((item, index) => (
          <div className="csr-card" key={index}>
            {isPlainObject(item) ? <GenericObject data={item} /> : <p>{String(item)}</p>}
          </div>
        ))}
      </div>
    );
  }
  if (isPlainObject(value)) return <GenericObject data={value} />;
  return <span>{String(value)}</span>;
};

const GenericObject = ({ data }) => (
  <dl className="csr-fields">
    {Object.entries(data).map(([key, value]) => (
      <div key={key}>
        <dt>{humanize(key)}</dt>
        <dd>
          <GenericValue value={value} />
        </dd>
      </div>
    ))}
  </dl>
);

const SaveBanner = ({ saved, kind }) => {
  if (!saved) return null;
  return (
    <p className={saved.ok ? "csr-save ok" : "csr-save warn"}>
      {saved.message || (saved.ok ? `${kind} saved.` : `${kind} were not saved.`)}
    </p>
  );
};

const StructuredBody = ({ data, onChoice, onSave, onCancel, onAddVisuals, onSkipVisuals }) => {
  if (Array.isArray(data)) {
    if (data[0]?.code || data[0]?.description) return <StandardsBlock data={{ standards: data }} />;
    if (data[0]?.stem || data[0]?.options) return <QuestionsBlock data={{ questions: data }} />;
    return <GenericValue value={data} />;
  }
  if (data.lookup === "states" || data.lookup === "standards" || data.lookup === "create-setup" || data.lookup === "create-question" || data.lookup === "questions" || data.lookup === "question-summary") {
    return <CatalogLookupBlock data={data} onChoice={onChoice} />;
  }
  if (Array.isArray(data.standards)) {
    return <StandardsBlock data={data} onSave={onSave} onCancel={onCancel} />;
  }
  if (Array.isArray(data.questions)) {
    return (
      <QuestionsBlock
        data={data}
        onSave={onSave}
        onCancel={onCancel}
        onAddVisuals={onAddVisuals}
        onSkipVisuals={onSkipVisuals}
      />
    );
  }
  if (data.imageUrl || (data.prompt && (data.style || data.altText))) return <ImageBlock data={data} />;
  if (Array.isArray(data.topics)) return <TopicsBlock data={data} />;
  return <GenericObject data={data} />;
};

export const parseStructuredReply = (text) => tryParse(text);

export function ChatStructuredReply({
  text,
  fallback = null,
  onChoice,
  onSaveStandards,
  onCancelStandards,
  onSaveQuestions,
  onCancelQuestions,
  onAddVisuals,
  onSkipVisuals,
}) {
  const data = tryParse(text);
  if (!data) return fallback;
  const savingQuestions = Array.isArray(data.questions) && data.pendingSave;
  return (
    <div className="csr">
      <SaveBanner saved={data.savedQuestions} kind="Questions" />
      <SaveBanner saved={data.savedStandards} kind="Standards" />
      <StructuredBody
        data={data}
        onChoice={onChoice}
        onSave={
          savingQuestions
            ? onSaveQuestions
              ? () => onSaveQuestions(data)
              : undefined
            : onSaveStandards
              ? () => onSaveStandards(data)
              : undefined
        }
        onCancel={savingQuestions ? onCancelQuestions : onCancelStandards}
        onAddVisuals={onAddVisuals ? () => onAddVisuals(data) : undefined}
        onSkipVisuals={onSkipVisuals ? () => onSkipVisuals(data) : undefined}
      />
    </div>
  );
}
